import React, { useEffect, useCallback, useMemo } from "react"; // Removido useState, adicionado useMemo
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import Form from "../../../../components/forms/Form";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import { kitItemFields } from "../../../../constants/forms/kitFields";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditKitItemPage = () => {
  const navigate = useNavigate();
  const { equipmentKitId, itemId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "equipments_kits_items",
  );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(kitItemFields),
    kitItemFields,
  );

  // --- Query para buscar os detalhes do Item do Kit ---
  const {
    data: kitItemData,
    isLoading: isLoadingKitItem,
    isError: isErrorKitItem,
  } = useQueryWithLoader({
    queryKey: ["equipment_kit_item", equipmentKitId, itemId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.equipmentsKits.items.getById(equipmentKitId, itemId),
      );
      return response.result;
    },
    enabled: !!equipmentKitId && !!itemId,
    staleTime: 5 * 60 * 1000,
  });

  // --- useEffect para preencher o formulário quando os dados do item do kit estiverem prontos ---
  useEffect(() => {
    if (kitItemData) {
      setFormData({
        kitItem: kitItemData,
      });
    }
  }, [kitItemData, setFormData]);

  // --- Mutação para atualizar o Item do Kit ---
  const updateKitItemMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.equipmentsKits.equipmentKitItemUpdate(equipmentKitId, itemId),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        navigate(`/aluguel/kits-equipamentos/${equipmentKitId}/itens`, {
          state: { type: "success" },
        });
        queryClient.invalidateQueries({
          queryKey: ["equipment_kit_item", equipmentKitId, itemId],
        });
        queryClient.invalidateQueries({
          queryKey: [
            "equipments_kits_items",
            equipmentKitId,
            filters,
            pagination,
          ],
        });
      }
    },
    onError: (error) => {
      console.error("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        kitItem: {
          ...cleanedData.kitItem,
        },
      };
      await updateKitItemMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar item do kit:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateKitItemMutation]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/kits-equipamentos/${equipmentKitId}/itens`);
  }, [navigate, equipmentKitId]);

  // --- Efeito para exibir notificações de estado da rota (PADRÃO) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário (PADRÃO) ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return kitItemFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição do Item do Kit"
        showBackButton={true}
        backUrl={`/aluguel/kits-equipamentos/${equipmentKitId}/itens`}
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Atualizar"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={isLoadingKitItem || updateKitItemMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditKitItemPage;
